import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { EntityWorkLogDialog } from '../worklog/EntityWorkLogDialog';
import {
  loadPayorNoaWorkLog,
  searchPayorNoaSettings,
  updatePayorNoaSettings,
  type PayorNoaSettings,
} from '../../services/payorNoaSettings';

type RuleKey = 'noa_include_location_codes' | 'noa_omit_admitting_diagnosis';

const RULES: { key: RuleKey; label: string }[] = [
  {
    key: 'noa_omit_admitting_diagnosis',
    label: 'Leave off admitting diagnosis',
  },
  { key: 'noa_include_location_codes', label: 'Include location codes' },
];

const RULE_LABELS = Object.fromEntries(
  RULES.map(rule => [rule.key, rule.label])
);

// A rule with no value is "Unset", not the work log's generic "(empty)".
const formatRuleValue = (_field: string, value: unknown) =>
  value === null || value === undefined ? 'Unset' : undefined;

const toValue = (rule: boolean | null) =>
  rule === null ? 'unset' : rule ? 'yes' : 'no';

/**
 * The NOA rules each payer carries.
 *
 * An agency's insurance follows its payer until that insurance sets its own
 * answer, so a rule set here reaches every agency that has not overridden it.
 * Unset means the payer has no opinion and NOAs go out as they always have.
 */
export function PayorsPanel() {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );
  const [search, setSearch] = useState('');
  const [payors, setPayors] = useState<PayorNoaSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  const latestRequest = useRef(0);
  const [historyFor, setHistoryFor] = useState<PayorNoaSettings | null>(null);
  // A new load identity makes the open dialog refetch, so a save shows up.
  const [historyVersion, setHistoryVersion] = useState(0);
  const loadHistory = useCallback(
    () => loadPayorNoaWorkLog(historyFor?.id ?? ''),

    [historyFor?.id, historyVersion]
  );

  const load = useCallback(
    async (term: string) => {
      const requestId = ++latestRequest.current;
      setLoading(true);
      try {
        const found = await searchPayorNoaSettings(term.trim());
        if (requestId === latestRequest.current) setPayors(found);
      } catch (error) {
        if (requestId !== latestRequest.current) return;
        setPayors([]);
        console.error('Failed to load the payors', error);
        showError('Could not load the payors.');
      } finally {
        if (requestId === latestRequest.current) setLoading(false);
      }
    },
    [showError]
  );

  useEffect(() => {
    const timer = setTimeout(() => void load(search), 300);
    return () => clearTimeout(timer);
  }, [load, search]);

  const change = async (payor: PayorNoaSettings, key: RuleKey, raw: string) => {
    const value = raw === 'unset' ? null : raw === 'yes';
    setSavingId(payor.id);
    try {
      const saved = await updatePayorNoaSettings(payor.id, { [key]: value });
      setPayors(rows => rows.map(row => (row.id === saved.id ? saved : row)));
      showSuccess(`Saved ${saved.payor_name}.`);
      setHistoryVersion(version => version + 1);
    } catch (error) {
      console.error('Failed to update the payor', error);
      showError('Could not save that payor.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payors</CardTitle>
        <CardDescription>
          NOA rules every agency's insurance follows for the payer unless that
          insurance overrides them. Unset leaves NOAs as they were.
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        <Input
          aria-label='Search payors'
          placeholder='Search by payer name or ID'
          value={search}
          onChange={event => setSearch(event.target.value)}
          className='w-full md:w-80'
        />
        {loading ? (
          <Loader2 className='h-4 w-4 animate-spin' aria-label='Loading' />
        ) : payors.length === 0 ? (
          <p className='text-sm text-muted-foreground'>No payors found.</p>
        ) : (
          <div className='overflow-auto'>
            <Table className='min-w-[760px]'>
              <TableHeader>
                <TableRow>
                  <TableHead>Payor</TableHead>
                  {RULES.map(rule => (
                    <TableHead key={rule.key}>{rule.label}</TableHead>
                  ))}
                  <TableHead>History</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payors.map(payor => (
                  <TableRow key={payor.id}>
                    <TableCell>
                      <div className='font-medium'>{payor.payor_name}</div>
                      <div className='text-xs text-muted-foreground'>
                        {payor.electronic_payor_id ?? 'No payer ID'}
                      </div>
                    </TableCell>
                    {RULES.map(rule => (
                      <TableCell key={rule.key}>
                        <select
                          aria-label={`${rule.label} for ${payor.payor_name}`}
                          className='h-9 rounded-md border border-input bg-background px-2 text-sm'
                          value={toValue(payor[rule.key])}
                          disabled={savingId === payor.id}
                          onChange={event =>
                            void change(payor, rule.key, event.target.value)
                          }
                        >
                          <option value='unset'>Unset</option>
                          <option value='yes'>Yes</option>
                          <option value='no'>No</option>
                        </select>
                      </TableCell>
                    ))}
                    <TableCell className='align-top'>
                      <button
                        type='button'
                        className='text-sm underline'
                        onClick={() => setHistoryFor(payor)}
                      >
                        History
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
      <EntityWorkLogDialog
        open={historyFor !== null}
        onOpenChange={open => {
          if (!open) setHistoryFor(null);
        }}
        load={loadHistory}
        title={`NOA rule history${historyFor ? `: ${historyFor.payor_name}` : ''}`}
        description="Changes to this payor's NOA rules, oldest first."
        fieldLabels={RULE_LABELS}
        formatValue={formatRuleValue}
      />
    </Card>
  );
}
